import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Heart, Users, Shield, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { supportedLivingImages } from '../utils/mockData';

const SupportedLiving = () => {
  const whoWeSupport = [
    'Long-term or severe mental health conditions',
    'Recurrent hospital admissions or complex needs',
    'Difficulties with daily living, routine, or medication adherence',
    'Social isolation or reduced community engagement'
  ];

  const supportTypes = [
    {
      icon: Home,
      title: 'Daily Living & Practical Support',
      items: [
        'Assistance with activities of daily living (ADLs)',
        'Support with personal care where required',
        'Establishing and maintaining healthy routines',
        'Support with budgeting, shopping, and household tasks'
      ]
    },
    {
      icon: Heart,
      title: 'Medication Support',
      items: [
        'Prompting or administration of prescribed medication',
        'Monitoring adherence and wellbeing',
        'Liaison with prescribers, GPs, and mental health services',
        'Supporting understanding of medication and side effects'
      ]
    },
    {
      icon: Users,
      title: 'Health & Appointments',
      items: [
        'Support to attend medical and mental health appointments',
        'Liaison with multidisciplinary teams',
        'Advocacy and communication support where required',
        'Coordination of care across services'
      ]
    },
    {
      icon: Clock,
      title: 'Wellbeing, Structure & Meaningful Activity',
      items: [
        'Encouraging engagement in meaningful activities and hobbies',
        'Supporting social inclusion and community participation',
        'Promoting physical activity and healthy lifestyle choices',
        'Supporting emotional wellbeing and recovery goals'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="heading-hero">Supported Living Services</h1>
              <p className="body-large">
                Empowering adults with mental health conditions to live safely and independently in the community with person-centered, recovery-focused support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-[#4A90E2] to-[#7B68BE]">
                    Make a Referral
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-2 border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white"
                  >
                    Learn About Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img 
                  src={supportedLivingImages[0]}
                  alt="Comfortable living space"
                  className="rounded-xl w-full h-48 object-cover shadow-lg"
                />
                <img 
                  src={supportedLivingImages[1]}
                  alt="Peaceful bedroom"
                  className="rounded-xl w-full h-64 object-cover shadow-lg"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img 
                  src={supportedLivingImages[2]}
                  alt="Independent living"
                  className="rounded-xl w-full h-64 object-cover shadow-lg"
                />
                <img 
                  src={supportedLivingImages[3]}
                  alt="Cozy atmosphere"
                  className="rounded-xl w-full h-48 object-cover shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-6 text-center">Our Supported Living Model</h2>
            <p className="body-large text-center mb-8">
              Three Spheres Healthcare provides supported living services for adults with enduring mental health conditions and mental health-related disabilities who require structured support to live safely and independently in the community.
            </p>
            <p className="body-medium text-center text-gray-600">
              Our supported living model is designed to promote choice, autonomy, and quality of life, while ensuring that risks are managed appropriately and individuals receive consistent, reliable care.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Support */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Who We Support</h2>
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm mb-8">
              <p className="body-medium text-gray-700 mb-6">
                We support adults who may experience:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {whoWeSupport.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-[#E8F4FC] rounded-lg p-4">
                    <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Client Groups */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-0 text-center hover:shadow-lg transition-all">
                <Users className="w-12 h-12 text-[#4A90E2] mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-3 text-gray-900">Adults with Mental Health Needs</h3>
                <p className="text-sm text-gray-600">
                  Specialist support for individuals with enduring mental health conditions
                </p>
              </Card>
              <Card className="p-6 border-0 text-center hover:shadow-lg transition-all">
                <Heart className="w-12 h-12 text-[#7B68BE] mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-3 text-gray-900">Adults with Complex Needs</h3>
                <p className="text-sm text-gray-600">
                  Comprehensive care for individuals with multiple or co-occurring conditions
                </p>
              </Card>
              <Card className="p-6 border-0 text-center hover:shadow-lg transition-all">
                <Home className="w-12 h-12 text-[#4A90E2] mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-3 text-gray-900">Transition Support</h3>
                <p className="text-sm text-gray-600">
                  Supporting individuals transitioning from hospital to community settings
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How We Support */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">How We Support</h2>
            <p className="body-large max-w-3xl mx-auto">
              Support is tailored to each individual and delivered in line with personalized care plans and risk assessments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {supportTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <Card key={index} className="p-8 border-0 hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="heading-3 mb-4">{type.title}</h3>
                  <ul className="space-y-3">
                    {type.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={18} />
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Focus */}
      <section className="section bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="w-16 h-16 text-[#4A90E2] mx-auto mb-6" />
            <h2 className="heading-2 mb-6">Our Focus</h2>
            <p className="body-large mb-8">
              Our focus is always on maximizing independence, reducing reliance on inpatient care, and enabling individuals to live lives that feel purposeful, safe, and valued.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6">
                <div className="text-3xl font-bold text-[#4A90E2] mb-2">Independence</div>
                <p className="text-sm text-gray-700">Maximizing autonomy and choice in daily life</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6">
                <div className="text-3xl font-bold text-[#7B68BE] mb-2">Safety</div>
                <p className="text-sm text-gray-700">Reducing crisis and hospital readmission</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6">
                <div className="text-3xl font-bold text-[#4A90E2] mb-2">Purpose</div>
                <p className="text-sm text-gray-700">Supporting meaningful and valued lives</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Ready to Make a Referral?</h2>
            <p className="body-large text-white/90">
              Contact us to discuss supported living options, make a referral, or learn more about our services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                  Contact Us
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#4A90E2]"
                >
                  View All Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportedLiving;
